import { Checkbox, Button, Switch, IconButton } from '@ds';
import { MdMarkEmailUnread, MdClose } from 'react-icons/md';
import { OrganType, DonorType } from '@/api/apis/case';
import { StreamChannelType } from '@/api/apis/chat';
import { ChatFilterState } from '../../../types';
import { prettify } from './utils';

type ChatFilterPanelProps = {
  draftFilters: ChatFilterState;
  setOrganTypes: (v: OrganType[]) => void;
  setDonorTypes: (v: DonorType[]) => void;
  setChatTypes: (v: StreamChannelType[]) => void;
  toggleUnreadOnly: () => void;
  applyFilters: () => void;
  clearAll: () => void;
  onClose?: () => void;
};

export const ChatFilterPanel = ({
  draftFilters,
  setOrganTypes,
  setDonorTypes,
  setChatTypes,
  toggleUnreadOnly,
  applyFilters,
  clearAll,
  onClose,
}: ChatFilterPanelProps) => {
  const organTypes: OrganType[] = Object.values(OrganType);
  const donorTypes: DonorType[] = Object.values(DonorType);
  const chatTypes: StreamChannelType[] = [
    StreamChannelType.General,
    StreamChannelType.Quote,
    StreamChannelType.OrganManagement,
    StreamChannelType.Logistics,
    StreamChannelType.Tmdx,
    StreamChannelType.OpoGeneral,
  ];

  const toggleFilter = <T,>(list: T[], v: T) => (list.includes(v) ? list.filter((x) => x !== v) : [...list, v]);

  return (
    <div className="w-[300px] border-r border-base-60 flex flex-col h-full bg-base-80 shadow-right">
      <div className="flex items-center justify-between px-3 py-2 border-b border-base-60">
        <span className="text-m-sb">Filters</span>
        <IconButton size="sm" variant="light" startContent={<MdClose />} aria-label="Close Filters" onPress={onClose} />
      </div>

      <div className="flex-1 overflow-y-auto p-4 flex flex-col gap-6">
        <section className="flex flex-col gap-2">
          <h4 className="text-m-sb mb-2">Organ Type</h4>
          {organTypes.map((o) => (
            <Checkbox
              key={o}
              size="sm"
              isSelected={draftFilters.organTypes.includes(o)}
              onValueChange={() => setOrganTypes(toggleFilter(draftFilters.organTypes, o))}
            >
              {prettify(o)}
            </Checkbox>
          ))}
        </section>

        <section className="flex flex-col gap-2">
          <h4 className="text-m-sb mb-2">Donor Type</h4>
          {donorTypes.map((d) => (
            <Checkbox
              key={d}
              size="sm"
              isSelected={draftFilters.donorTypes.includes(d)}
              onValueChange={() => setDonorTypes(toggleFilter(draftFilters.donorTypes, d))}
            >
              {prettify(d)}
            </Checkbox>
          ))}
        </section>

        <section className="flex flex-col gap-2">
          <h4 className="text-m-sb mb-2">Chat Type</h4>
          {chatTypes.map((t) => (
            <Checkbox
              key={t}
              size="sm"
              isSelected={draftFilters.chatTypes.includes(t)}
              onValueChange={() => setChatTypes(toggleFilter(draftFilters.chatTypes, t))}
            >
              {t === StreamChannelType.OpoGeneral ? 'OPO' : prettify(t)}
            </Checkbox>
          ))}
        </section>
        <div className="mt-auto flex items-center justify-between pt-4">
          <span className="text-m-sb flex items-center gap-2">
            <MdMarkEmailUnread size={18} />
            Show Unread messages
          </span>
          <Switch isSelected={draftFilters.unreadOnly} onChange={toggleUnreadOnly} />
        </div>
      </div>
      <div className="mb-4 mx-4 flex flex-col gap-2">
        <Button onPress={clearAll} size="sm" variant="light">
          Clear All
        </Button>
        <Button onPress={applyFilters} size="sm" variant="solid">
          Apply Filters
        </Button>
      </div>
    </div>
  );
};
